import React from 'react';
import { motion } from 'framer-motion';
import { 
  DocumentArrowDownIcon,
  EyeIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';

const References: React.FC = () => {
  const references = [
    {
      company: "Loxport",
      date: "October 1, 2026",
      quote: "He took the Arabic-first marketplace for heavy transport equipment in Egypt from first commit to App Store submission in under six months. We thank him for his excellent work at Loxport.",
      logo: "/companies/loxport.jpg",
      certificateFile: "/references/Loxport_Reference_Letter.pdf"
    },
    {
      company: "B+P Building Partners Group",
      date: "January 3, 2023",
      quote: "Thanks to his pronounced talent for organisation, he was always able to grasp even difficult situations immediately and find solutions quickly. We thank him for his excellent performance.",
      logo: "/companies/bp-group.jpg",
      certificateFile: "/references/BP_Arbeitszeugnis.pdf"
    }
  ];

  return (
    <section id="references" className="py-16 bg-white border-t border-gray-200">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Reference Letters
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Official employer references documenting performance and achievements
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {references.map((ref, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -5 }}
              className="bg-gray-50 rounded-lg p-6 hover:shadow-lg transition-all duration-300"
            >
              {/* Company Header */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <img
                    src={ref.logo}
                    alt={`${ref.company} logo`}
                    className="w-8 h-8 object-contain"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                    }}
                  />
                  <div>
                    <h4 className="font-semibold text-gray-900 text-sm">
                      {ref.company}
                    </h4>
                    <div className="flex items-center text-xs text-gray-500">
                      <CalendarIcon className="h-3 w-3 mr-1" />
                      {ref.date}
                    </div>
                  </div>
                </div>
              </div>

              {/* Quote */}
              <blockquote className="text-gray-600 text-sm italic leading-relaxed mb-6">
                "{ref.quote}"
              </blockquote>

              {/* Actions */}
              <div className="flex space-x-2">
                <motion.a
                  href={ref.certificateFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-medium rounded transition-colors"
                >
                  <EyeIcon className="h-3 w-3 mr-1" />
                  View
                </motion.a>
                <motion.a
                  href={ref.certificateFile}
                  download
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-gray-600 hover:bg-gray-700 text-white text-xs font-medium rounded transition-colors"
                >
                  <DocumentArrowDownIcon className="h-3 w-3 mr-1" />
                  Download
                </motion.a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default References;